import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RejectionModalContext, EditModalContext, ActionCommand } from '../types';
import { GenericDraftState, GenericDraftHistory } from './types';

// Reuse the same context interface for accept modal for consistency
export type AcceptModalContext = RejectionModalContext;

// Export ActionUIState interface for use in store/types.ts
export interface ActionUIState {
  expandedCardId: string | null; // Track which card is currently expanded
  rejectionModal: {
    isOpen: boolean;
    context: RejectionModalContext | null;
  };
  acceptModal: {
    isOpen: boolean;
    context: AcceptModalContext | null;
  };
  editModal: {
    isOpen: boolean;
    context: EditModalContext | null;
  };
  pendingCommand?: {
    actionId: string;
    command: ActionCommand;
  } | null;
  // Draft persistence state - using generic types
  drafts: Record<string, GenericDraftState>;
  histories: Record<string, GenericDraftHistory>;
}

// Initial state
const initialState: ActionUIState = {
  expandedCardId: null,
  rejectionModal: {
    isOpen: false,
    context: null,
  },
  acceptModal: {
    isOpen: false,
    context: null,
  },
  editModal: {
    isOpen: false,
    context: null,
  },
  drafts: {},
  histories: {},
};

// Create the slice
export const actionUISlice = createSlice({
  name: 'actions/ui',
  initialState,
  reducers: {
    expandCard: (state, action: PayloadAction<string>) => {
      // If a different card was already expanded, collapse it first
      state.expandedCardId = action.payload;
    },

    collapseCard: (state, action: PayloadAction<string | undefined>) => {
      // Only collapse if no ID provided or it matches the currently expanded card
      if (!action.payload || state.expandedCardId === action.payload) {
        state.expandedCardId = null;
      }
    },

    toggleCardExpansion: (state, action: PayloadAction<string>) => {
      const cardId = action.payload;
      // If this card is already expanded, collapse it
      // Otherwise expand it (collapsing any previously expanded card)
      state.expandedCardId = state.expandedCardId === cardId ? null : cardId;
    },

    openRejectionModal: (state, action: PayloadAction<RejectionModalContext>) => {
      state.rejectionModal.isOpen = true;
      state.rejectionModal.context = action.payload;
    },

    closeRejectionModal: state => {
      state.rejectionModal.isOpen = false;
      state.rejectionModal.context = null;
    },

    openAcceptModal: (state, action: PayloadAction<AcceptModalContext>) => {
      state.acceptModal.isOpen = true;
      state.acceptModal.context = action.payload;
    },

    closeAcceptModal: state => {
      state.acceptModal.isOpen = false;
      state.acceptModal.context = null;
    },

    openEditModal: (state, action: PayloadAction<EditModalContext>) => {
      state.editModal.isOpen = true;
      state.editModal.context = action.payload;
    },

    closeEditModal: state => {
      state.editModal.isOpen = false;
      state.editModal.context = null;
    },

    setPendingCommand: (
      state,
      action: PayloadAction<{ actionId: string; command: ActionCommand }>,
    ) => {
      state.pendingCommand = action.payload;
    },

    clearPendingCommand: state => {
      state.pendingCommand = null;
    },

    resetActionUIState: () => initialState,

    // Draft management - now generic
    updateDraft: (
      state,
      action: PayloadAction<{
        actionId: string;
        data: Record<string, any>;
        userInitiated?: boolean;
      }>,
    ) => {
      const { actionId, data, userInitiated = true } = action.payload;

      if (!state.drafts[actionId]) {
        // Initialize draft for this action
        state.drafts[actionId] = {
          actionId,
          data,
          lastSaved: Date.now(),
          isDirty: true,
        };

        // Only create history if it doesn't exist
        if (!state.histories[actionId]) {
          state.histories[actionId] = {
            past: [],
            present: {
              data,
              timestamp: Date.now(),
            },
            future: [],
          };
        }
      } else {
        // Update existing draft - merge data
        const currentDraft = state.drafts[actionId];
        const previousData = currentDraft.data;
        const hasChanges = Object.keys(data).some(key => data[key] !== previousData[key]);

        if (hasChanges) {
          currentDraft.data = { ...currentDraft.data, ...data };
          currentDraft.isDirty = true;
          currentDraft.lastSaved = Date.now();

          // Update history ONLY when content actually changes AND it's user-initiated
          if (userInitiated) {
            // Ensure history exists
            if (!state.histories[actionId]) {
              state.histories[actionId] = {
                past: [],
                present: null,
                future: [],
              };
            }

            const history = state.histories[actionId];
            if (history) {
              const snapshot = {
                data: currentDraft.data,
                timestamp: Date.now(),
              };

              // Only add to history if it's different from current present
              const isDifferent =
                !history.present ||
                JSON.stringify(history.present.data) !== JSON.stringify(snapshot.data);

              if (isDifferent) {
                if (history.present) {
                  history.past.push(history.present);
                }
                history.present = snapshot;
                history.future = [];

                // Limit history to last 50 entries per action
                if (history.past.length > 50) {
                  history.past = history.past.slice(-50);
                }
              }
            }
          }
        }
      }
    },

    clearDraft: (state, action: PayloadAction<string>) => {
      const actionId = action.payload;
      // Clear draft for specific action
      delete state.drafts[actionId];
      // Clear history for specific action
      delete state.histories[actionId];
    },

    clearAllDraftsForAction: (state, action: PayloadAction<string>) => {
      const actionId = action.payload;
      // Clear everything for this action
      delete state.drafts[actionId];
      delete state.histories[actionId];
    },

    resetDraft: (
      state,
      action: PayloadAction<{ actionId: string; originalData: Record<string, any> }>,
    ) => {
      const { actionId, originalData } = action.payload;

      // Reset draft to original data
      state.drafts[actionId] = {
        actionId,
        data: originalData,
        lastSaved: Date.now(),
        isDirty: false,
      };

      // Clear history
      state.histories[actionId] = {
        past: [],
        present: {
          data: originalData,
          timestamp: Date.now(),
        },
        future: [],
      };
    },

    loadDraftForAction: (
      state,
      action: PayloadAction<{ actionId: string; draft?: GenericDraftState }>,
    ) => {
      const { actionId, draft } = action.payload;
      if (draft) {
        // Load provided draft (e.g., from cross-tab sync)
        state.drafts[actionId] = draft;
      }
      // Otherwise handled by middleware to load from persistence
    },

    initializeDraft: (
      state,
      action: PayloadAction<{ actionId: string; data: Record<string, any> }>,
    ) => {
      const { actionId, data } = action.payload;

      // Only initialize if draft doesn't exist
      if (!state.drafts[actionId]) {
        state.drafts[actionId] = {
          actionId,
          data,
          lastSaved: Date.now(),
          isDirty: false, // Initial load is not dirty
        };

        // Initialize history
        state.histories[actionId] = {
          past: [],
          present: { data, timestamp: Date.now() },
          future: [],
        };
      }
    },

    undo: (state, action: PayloadAction<string>) => {
      const actionId = action.payload;
      const history = state.histories[actionId];
      const draft = state.drafts[actionId];

      if (!history || history.past.length === 0) return;

      const previous = history.past[history.past.length - 1];
      const current = history.present;

      history.past = history.past.slice(0, -1);
      history.present = previous;

      if (current) {
        history.future = [current, ...history.future];
      }

      // Update draft for this action (system-initiated, don't add to history)
      if (draft && previous) {
        draft.data = previous.data;
        draft.isDirty = true;
        draft.lastSaved = Date.now();
      }
    },

    redo: (state, action: PayloadAction<string>) => {
      const actionId = action.payload;
      const history = state.histories[actionId];
      const draft = state.drafts[actionId];

      if (!history || history.future.length === 0) return;

      const next = history.future[0];
      const current = history.present;

      history.future = history.future.slice(1);
      history.present = next;

      if (current) {
        history.past = [...history.past, current];
      }

      // Update draft for this action (system-initiated, don't add to history)
      if (draft && next) {
        draft.data = next.data;
        draft.isDirty = true;
        draft.lastSaved = Date.now();
      }
    },

    // Cleanup actions
    cleanupOldDrafts: (state, action: PayloadAction<{ maxAge?: number; keepDirty?: boolean }>) => {
      const { maxAge = 7 * 24 * 60 * 60 * 1000, keepDirty = true } = action.payload; // 7 days default
      const now = Date.now();

      Object.entries(state.drafts).forEach(([actionId, draft]) => {
        // Skip dirty drafts if keepDirty is true
        if (keepDirty && draft.isDirty) return;

        // Remove drafts older than maxAge
        if (now - draft.lastSaved > maxAge) {
          delete state.drafts[actionId];
          delete state.histories[actionId];
          // Optionally keep versions for audit trail
          // delete state.versions[actionId];
        }
      });
    },

    cleanupOrphanedDrafts: (state, action: PayloadAction<string[]>) => {
      // Remove drafts for actions that no longer exist
      const validActionIds = action.payload;

      Object.keys(state.drafts).forEach(actionId => {
        if (!validActionIds.includes(actionId)) {
          delete state.drafts[actionId];
          delete state.histories[actionId];
        }
      });
    },
  },
});

// Export actions
export const {
  expandCard,
  collapseCard,
  toggleCardExpansion,
  openRejectionModal,
  closeRejectionModal,
  openAcceptModal,
  closeAcceptModal,
  openEditModal,
  closeEditModal,
  setPendingCommand,
  clearPendingCommand,
  resetActionUIState,
  // Draft actions
  updateDraft,
  clearDraft,
  clearAllDraftsForAction,
  resetDraft,
  loadDraftForAction,
  initializeDraft,
  undo,
  redo,
  cleanupOldDrafts,
  cleanupOrphanedDrafts,
} = actionUISlice.actions;

// Export the reducer
export default actionUISlice.reducer;
